<template>
  <div class="streams-schedule">
    <h1 class="streams-schedule__title">Transliacijos</h1>
    <Navigation :date="date" @set-date="handleSetDate" />
    <Schedule :schedule="schedule" />
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import isBetween from 'dayjs/plugin/isBetween';
import getPageMeta from '@custom/core/utils/getPageMeta';
import getSchedule from '@custom/core/apollo/services/getSchedule';
import { convertToLocal } from '@root/common/filters/dateFormater';

import Navigation from '@custom/components/common/Navigation.vue';
import Schedule from '@custom/components/common/Schedule.vue';

interface Attribute {
  key: string;
  value: string;
}

interface Data {
  date: string;
  offset: number;
  limit: number;
  schedule: Array<Record<string, any>>;
}

interface Methods {
  getSchedule: (params: Record<string, any>) => Promise<Array<Record<string, any>>>;
  handleSetDate: (date: string) => void;
}

export default Vue.extend<Data, Methods, unknown, unknown>({
  components: {
    Navigation,
    Schedule,
  },
  data() {
    return {
      date: dayjs().format('YYYY-MM-DD'),
      offset: 0,
      limit: 0,
      schedule: [],
      selected: 7,
    };
  },
  head() {
    const { locale } = this.$channelConfig('settings');
    const meta = getPageMeta(locale, this.$route.path);
    return {
      title: meta.title,
      description: meta.title,
      meta: [
        {
          hid: 'og_title',
          property: 'og:title',
          content: meta.title,
        },
        {
          hid: 'og_description',
          property: 'og:description',
          content: meta.description,
        },
      ],
    };
  },
  watch: {
    date: {
      async handler(value: boolean) {
        if (value) {
          this.schedule = await this.getSchedule({ date: this.date, offset: this.offset, limit: this.limit });
        }
      },
      immediate: true,
    },
  },
  async created() {
    const { lang } = this.$channelConfig('settings');
    dayjs.locale(lang);
    dayjs.extend(isBetween);

    this.schedule = await this.getSchedule({ date: this.date, offset: this.offset, limit: this.limit });
  },
  methods: {
    async getSchedule(params) {
      const { date, offset, limit } = params;

      const grouppedSchedule: Array<Record<string, any>> = [];
      const dateStart = convertToLocal(`${date} 00:00:00`);
      const dateEnd = convertToLocal(`${date} 23:59:59`);
      const data = await getSchedule({ dateStart, dateEnd, offset, limit });

      const headlines = data.headlines.items;

      headlines.sort((a: any, b: any) => {
        const eventStartA = a.attributes.find((attr: Attribute) => attr.key === 'streamEventStart')?.value;
        const eventStartB = b.attributes.find((attr: Attribute) => attr.key === 'streamEventStart')?.value;
        return dayjs(eventStartA).unix() - dayjs(eventStartB).unix();
      });

      headlines.forEach((item: any) => {
        const streamEventStart: Attribute | null = item.attributes.find((attribute: { key: string }) => attribute.key === 'streamEventStart') || null;
        const streamEventEnd: Attribute | null = item.attributes.find((attribute: { key: string }) => attribute.key === 'streamEventEnd') || null;
        const startTime = dayjs(streamEventStart?.value).format('HH:mm');

        let group = grouppedSchedule.find((g) => g.time === startTime);
        let groupCreated = false;
        if (!group) {
          group = { time: startTime, items: [] };
          groupCreated = true;
        }

        // Check if stream is live
        if (dayjs().isBetween(dayjs(streamEventStart?.value), dayjs(streamEventEnd?.value), 's')) {
          item.relatedEntities = {};
          item.relatedEntities.hasStream = true;
        }

        // Check if stream is upcomming
        if (dayjs().isBetween(dayjs(streamEventStart?.value).subtract(30, 'minutes'), dayjs(streamEventStart?.value), 's')) {
          item.settings = [];
          item.settings.push({ key: 'streamSoon', value: true });
        }

        group.items.push({ ...item, href: `https://www.delfi.lt/a/${item.id}` });

        if (groupCreated) {
          grouppedSchedule.push(group);
        } else {
          grouppedSchedule.map((g) => (g.time === startTime ? group : g));
        }
      });

      return grouppedSchedule;
    },
    handleSetDate(date) {
      this.date = date;
    },
  },
});
</script>
<style lang="scss">
.streams-schedule {
  &__title {
    margin: 0 0 25px 0;
    font-size: 30px;
    line-height: 36px;

    @include media-breakpoint-down(xs) {
      margin: 0 0 14px 0;
      font-size: 22px;
      line-height: 26px;
    }
  }
}
</style>
